import { RowClickedEvent, RowDoubleClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { CreateCellClickHandler, CustomCellClickAction, useCellChangeHandler } from 'src/utils/grid/GridHandlerContent';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef } from 'src/utils/grid/gridStyleUtils';
import useUiuxStore, { CommonCodeModalProps } from 'src/store/useUiuxStore';

import {
	createBaseColumnDefs,
	creatColumnDefs,
	defaultColumnState,
	COLUMN_STATE_KEY,
} from './ColumnsDef_SYS1010_R01';
import { SYS1010_R01Dto } from './Dto_SYS1010_R01';
import { useSYS1010_R01Store } from './Store_SYS1010_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

interface Props {
	gridRef: React.RefObject<AgGridReact<SYS1010_R01Dto> | null>;
	defaultSYS1010_R01HeaderRow: SYS1010_R01Dto;
}

export const Table_SYS1010_R01 = (props: Props) => {
	const { gridRef, defaultSYS1010_R01HeaderRow } = props;

	const {
		majorSYS1010_R01List,
		minorSYS1010_R01List,
		selectedMajorSYS1010_R01,
		setSelectedMajorSYS1010_R01,
		initSelectedMajorSYS1010_R01,
		selectedMinorSYS1010_R01List,
		setSelectedMinorSYS1010_R01List,
		updateMinorSYS1010_R01List,
		originalMinorSYS1010_R01List,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = useSYS1010_R01Store();

	const { setCommonCodeModalProps } = useUiuxStore();

	const clickTimer = useRef<NodeJS.Timeout | null>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		paddedGridData,
		createRowClassRules,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'majorCd',
		gridData: majorSYS1010_R01List,
		defaultGridData: defaultSYS1010_R01HeaderRow,
		selectedRow: selectedMajorSYS1010_R01,
	});

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'majorCd')(params), []);

	const baseColumnDefs = useMemo(() => createBaseColumnDefs(getCellStyle), [getCellStyle]);

	const columnDefs = useMemo(
		() => creatColumnDefs(baseColumnDefs, columnState),
	[baseColumnDefs, columnState]);

	const handleRowClick = useCallback((e: RowClickedEvent<SYS1010_R01Dto>) => {
		if (clickTimer.current) {
			clearTimeout(clickTimer.current);
			clickTimer.current = null;
		}

		clickTimer.current = setTimeout(() => {
			const rowData = e.data;
			if (e.node.rowPinned === 'bottom') return;
			if (!rowData?.majorCd) return;

			if (selectedMajorSYS1010_R01 && selectedMajorSYS1010_R01.majorCd === rowData.majorCd) {
				setSelectedMajorSYS1010_R01(undefined);
			} else {
				setSelectedMajorSYS1010_R01(
					majorSYS1010_R01List.find((value) => value.majorCd === rowData.majorCd),
				);
			}
			setDetailScrollTop(null);
			setSelectedMinorSYS1010_R01List([]);
		}, 100);
	}, [selectedMajorSYS1010_R01, majorSYS1010_R01List, setSelectedMajorSYS1010_R01, setSelectedMinorSYS1010_R01List, setDetailScrollTop]);

	const handleRowDoubleClick = useCallback((e: RowDoubleClickedEvent<SYS1010_R01Dto>) => {
		if (clickTimer.current) {
			clearTimeout(clickTimer.current);
			clickTimer.current = null;
		}

		if (e.node.rowPinned === 'bottom') return;

		const rowData = e.data;
		if (!rowData?.majorCd) return;

		const foundData = majorSYS1010_R01List.find((value) => value.majorCd === rowData.majorCd);
		if (foundData) {
			setCommonCodeModalProps({ data: foundData } as CommonCodeModalProps);
		}
	}, [majorSYS1010_R01List, setCommonCodeModalProps]);

	const cellClickActions: CustomCellClickAction<SYS1010_R01Dto>[] = useMemo(() => [], []);
	const handleCellClick = CreateCellClickHandler(minorSYS1010_R01List, 'valueIndex', cellClickActions);

	const handleCellChange = useCellChangeHandler({
		dataList: minorSYS1010_R01List,
		originalDataList: originalMinorSYS1010_R01List,
		updateRow: updateMinorSYS1010_R01List,
	});

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		majorCd: `${paddedGridData.length.toLocaleString()}건`,
		editable: false,
	}]), [paddedGridData.length]);

	useEffect(() => { ensureSelectedRow(); }, [majorSYS1010_R01List, selectedMajorSYS1010_R01]);

	return (
		<GridContextWrapper<SYS1010_R01Dto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedMajorSYS1010_R01}
			defaultRowData={defaultSYS1010_R01HeaderRow}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState(baseColumnDefs)}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SYS1010_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<SYS1010_R01Dto>({
					idKey: 'majorCd',
					selectedList: selectedMajorSYS1010_R01 ? [selectedMajorSYS1010_R01.majorCd] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onRowDoubleClicked={handleRowDoubleClick}
				onCellKeyDown={undefined}
				onCellValueChanged={handleCellChange}
				pinnedBottomRowData={pinnedBottomRow}
				onCellClicked={handleCellClick}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
